import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  AuthConfig,
  ControlCodes,
  PartnerAdvisory,
  pluralize,
  queries,
  service,
  useAuthConfig,
  useDebounceState,
} from "@theprelude/core";
import {
  CloseIcon,
  IconButton,
  Input,
  notifyError,
  SearchIcon,
} from "@theprelude/ds";
import { useMemo, useRef, useState } from "react";
import { Advisory } from "@/components/threat-intel/advisory";
import { Filter } from "@/components/threat-intel/filter";
import { createQuery } from "@/lib/create-query";

export function Advisories() {
  const authConfig = useAuthConfig();
  const searchTerm = useDebounceState<string>("");
  const [filterTags, setFilterTags] = useState(new Set<string>());

  const queryInfo = advisoriesQuery(authConfig).useSuspenseQuery();

  const { advisories = [], tags = [] } = queryInfo.data ?? {};

  const filteredAdvisories = useMemo(() => {
    const needle = searchTerm.debouncedValue.trim().toLocaleLowerCase();
    let results = advisories;
    if (needle) {
      results = advisories.filter(
        (advisory) =>
          advisory.name.toLocaleLowerCase().includes(needle) ||
          advisory.description.toLocaleLowerCase().includes(needle) ||
          advisory.slug.toLocaleLowerCase().includes(needle),
      );
    }
    if (filterTags.size > 0) {
      results = results.filter((advisory) =>
        advisory.tags.some((t) => filterTags.has(t)),
      );
    }
    return results;
  }, [advisories, searchTerm.debouncedValue, filterTags]);

  return (
    <main className="flex w-full flex-1 flex-col gap-4 overflow-hidden">
      <section className="flex flex-col gap-4 p-6 pb-0">
        <div className="flex justify-end">
          <div className="flex flex-row items-center gap-2">
            <label className="text-xs text-secondary-foreground">Tags</label>
            <Filter
              label="tag"
              options={tags}
              selection={filterTags}
              onChangeSelection={setFilterTags}
            />
          </div>
        </div>
        <SearchInput
          text={searchTerm.value}
          onChangeText={searchTerm.setDelayed}
        />
        <div className="text-xs text-secondary-foreground">
          {advisories.length} {pluralize(advisories.length, "threat")} published
          in the last 30 days
        </div>
      </section>
      <section className="w-full flex-1 overflow-hidden">
        <VirtualizedAdvisoryList
          advisories={filteredAdvisories}
          searchTerm={searchTerm.value}
        />
      </section>
    </main>
  );
}

function SearchInput({
  text,
  onChangeText,
}: {
  text: string;
  onChangeText: (text: string) => void;
}) {
  return (
    <Input
      className="border-stroke-line"
      value={text}
      onChange={(ev) => onChangeText(ev.target.value)}
      before={<SearchIcon className="text-secondary-foreground" />}
      after={
        <IconButton
          icon={<CloseIcon />}
          onClick={() => {
            onChangeText("");
          }}
        />
      }
      placeholder="Search for an advisory by name, description or unique ID"
    />
  );
}

function VirtualizedAdvisoryList({
  advisories,
  searchTerm,
}: {
  advisories: PartnerAdvisory[];
  searchTerm: string;
}) {
  const navigate = useNavigate();
  const authConfig = useAuthConfig();

  const generate = useMutation({
    mutationFn: async ({
      advisory,
      forceAI,
    }: {
      advisory: PartnerAdvisory;
      forceAI: boolean;
    }) => {
      return await service(authConfig).generate.generateFromPartnerAdvisory(
        ControlCodes.CROWDSTRIKE,
        advisory.id,
        forceAI,
      );
    },
    onSuccess: async ({ job_id }, { advisory }) => {
      await navigate({
        to: "/threat-intel/$id",
        params: { id: job_id },
        search: { source_id: advisory.slug.toUpperCase() },
      });
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to import the advisory",
        error,
        dismissible: true,
      });
    },
  });

  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: advisories.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 269,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent?.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
    gap: 16,
  });

  return (
    <div ref={parentRef} className="h-full overflow-y-auto p-6 pt-0">
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const advisory = advisories[virtualItem.index] as PartnerAdvisory;
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: "absolute",
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <Advisory
                advisory={advisory}
                disabled={generate.isPending}
                loading={
                  generate.isPending &&
                  generate.variables.advisory.id === advisory.id
                }
                searchTerm={searchTerm}
                onGenerate={(forceAI) => generate.mutate({ advisory, forceAI })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getAdvisoriesData(advisories: PartnerAdvisory[]) {
  const tags = advisories.reduce((acc, advisory) => {
    advisory.tags.forEach((tag) => acc.add(tag));
    return acc;
  }, new Set<string>());

  return {
    advisories,
    tags: [...tags].sort((a, b) => a.localeCompare(b)),
  };
}

const advisoriesQuery = (authConfig: AuthConfig) =>
  createQuery({
    queries: [
      queries.partner.partnerAdvisories(authConfig, ControlCodes.CROWDSTRIKE),
    ],
    queryFn: getAdvisoriesData,
  });
